// shared/components/pillar-table/pagination-sorting/usePillarTablePaginationSorting.tsx

import { FormikContext } from "formik";
import queryString from "query-string";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  OrderTypes,
  type PaginationRequest,
} from "shared/api/types/pagination";
import { defaultPageSize } from "shared/components/pillar-table/pagination-sorting/PillarTablePagination";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { type FiltersRequest } from "shared/filter-where-clause";

const paginationFromUrl = (
  router: ReturnType<typeof useCustomRouter>,
  urlParams?: boolean,
): PaginationRequest => {
  if (!urlParams) {
    return {
      page: 1,
      pageSize: defaultPageSize,
    };
  }
  const query = queryString.parseUrl(router.asPath, {
    arrayFormat: "bracket",
  });
  const page = query.query.page ? Number(query.query.page) : 1;
  const pageSize = query.query.pageSize
    ? Number(query.query.pageSize)
    : defaultPageSize;

  const getFirstValue = (param: unknown) =>
    Array.isArray(param) ? param[0] : param;

  const orderBy = getFirstValue(query.query.orderBy) ?? undefined;
  const orderType = getFirstValue(query.query.orderType) ?? undefined;

  return {
    page,
    pageSize,
    orderByKey: orderBy,
    orderType,
  };
};

export const usePillarTablePaginationSorting = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>({
  filtersRequest,
  pagination,
  urlParams,
}: {
  filtersRequest: FiltersRequest;
  pagination?: boolean;
  urlParams?: boolean;
}) => {
  const router = useCustomRouter();
  const formik = useContext(FormikContext);

  const [paginationState, setPaginationState] = useState<
    PaginationRequest | undefined
  >(pagination ? paginationFromUrl(router, urlParams) : undefined);

  // const handleSort = ({ orderByKey: orderBy, orderType }: OrderByRequest) => {
  //   setPaginationState((prevState) => ({
  //     page: prevState!.page,
  //     pageSize: prevState!.pageSize,
  //     orderByKey: orderBy,
  //     orderType,
  //   }));
  // };

  const toggleSorting = useCallback(
    (fieldName: string) => {
      {
        if (
          paginationState?.orderByKey &&
          paginationState.orderByKey !== fieldName
        ) {
          console.warn(
            "This should not happen, orderByKey is set but does not match the fieldName",
          );
          return;
        }

        if (
          !paginationState?.orderType ||
          paginationState.orderType === OrderTypes.asc
        ) {
          // handleSort({ orderByKey: fieldName, orderType: OrderTypes.desc });
          setPaginationState((prevState) => ({
            ...prevState!,
            orderByKey: fieldName,
            orderType: OrderTypes.desc,
          }));
        } else if (paginationState.orderType === OrderTypes.desc) {
          // handleSort({ orderByKey: fieldName, orderType: OrderTypes.asc });
          setPaginationState((prevState) => ({
            ...prevState!,
            orderByKey: fieldName,
            orderType: OrderTypes.asc,
          }));
        }
      }
    },
    [paginationState, setPaginationState],
  );

  // Update URL parameters when pagination state or filters change.
  useEffect(() => {
    if (urlParams) {
      const query = queryString.parseUrl(router.asPath, {
        arrayFormat: "bracket",
      });
      const string = queryString.stringify(
        {
          ...query.query,
          page: pagination ? paginationState!.page : undefined,
          pageSize: pagination ? paginationState!.pageSize : undefined,
          orderByKey: pagination ? paginationState!.orderByKey : undefined,
          orderType:
            pagination && paginationState!.orderByKey
              ? paginationState!.orderType
              : undefined,
          ...filtersRequest,
        },
        {
          skipEmptyString: true,
          skipNull: true,
          arrayFormat: "bracket",
        },
      );
      router.replace(`${query.url}?${string}`);
    }
  }, [paginationState, filtersRequest]);

  useEffect(() => {
    if (formik) {
      formik.setFieldValue("pagination", paginationState);
    }
  }, [paginationState]);

  return {
    paginationState,
    setPaginationState,
    toggleSorting,
  };
};

export default usePillarTablePaginationSorting;
